// plan-modusign-style-acceptance §Phase1 — e-Sign Platform shared types.
//
// Consumed by:
//   - apps/api (NestJS service / controller DTOs)
//   - apps/windows (template designer + document management)
//   - apps/portal (Phase 2 — public signing page)
//
// Design decisions:
//   #2  대면(IN_PERSON) 서명만 (Phase 1)
//   #3  베이스 PDF 강제 (pdfS3Key NOT NULL)
//   #4  수정 시 version 증가, 옛 버전 freeze
//   #5  만료일 없음 — COMPLETED 시 signToken invalidate
// 추가수정 2차(26-07-27) 정정4 — KST 오프셋 상수는 같은 패키지의 정본(kst.ts)에서.
// 아래 signatureDocumentDisplayName 이 `9 * 3600_000` 을 인라인으로 갖고 있던
// 것을 제거한다(리포에 흩어져 있던 +9h 중복 한 벌 회수).
import { KST_OFFSET_MS } from './kst.js';
/**
 * 입력 칸 글자 크기(캔버스 px). `fontSize` 가 있으면 그대로 쓰되 칸 높이를 넘지 않게, 없으면
 * 그 화면의 예전 규칙(`fallback`). 콘솔 미리 채우기 · 이미지 내보내기 · 서명 페이지 · 태블릿이
 * 같은 규칙을 쓴다(서버 PDF 는 pt 로 바꿔 같은 값을 쓴다).
 */
export function signatureFieldFontPx(field, fallback) {
    if (!field.fontSize || field.fontSize <= 0)
        return fallback;
    return Math.max(6, Math.min(field.fontSize, Math.max(6, field.h)));
}
/**
 * 손가락으로 누르는 영역 — 작은 칸은 `min`(기본 44px)까지 넓히되 **이웃 칸과 겹치지 않게**
 * 둘 사이 한가운데까지만 넓힌다. 2026-10-20 오너 — "작은 입력박스들은 꼭 아래로 밀려나 있어서
 * 헷갈려. 모바일은 더 심해": 예전엔 17px 행의 칸을 44px 로 키워 그렸고, 다음 행의 칸이
 * 이 행을 거의 다 덮어 **이 행을 누르면 다음 행에 입력**됐다. 보이는 상자는 칸 그대로 두고
 * 이 영역은 누르는 데만 쓴다.
 *
 * 이웃 판정은 넓힌 뒤의 범위로 한다(대각선 이웃끼리도 겹치지 않게 보수적으로).
 */
export function signatureFieldTouchRects(fields, min = 44) {
    const want = fields.map((f) => ({
        f,
        dx: Math.max(0, (min - f.w) / 2),
        dy: Math.max(0, (min - f.h) / 2),
    }));
    const out = new Map();
    for (const a of want) {
        const { f } = a;
        let top = f.y - a.dy;
        let bottom = f.y + f.h + a.dy;
        let left = f.x - a.dx;
        let right = f.x + f.w + a.dx;
        for (const b of want) {
            if (b === a)
                continue;
            const g = b.f;
            const xOverlap = f.x - a.dx < g.x + g.w + b.dx && g.x - b.dx < f.x + f.w + a.dx;
            const yOverlap = f.y - a.dy < g.y + g.h + b.dy && g.y - b.dy < f.y + f.h + a.dy;
            if (xOverlap) {
                if (g.y + g.h <= f.y)
                    top = Math.max(top, (g.y + g.h + f.y) / 2);
                else if (g.y >= f.y + f.h)
                    bottom = Math.min(bottom, (f.y + f.h + g.y) / 2);
            }
            if (yOverlap) {
                if (g.x + g.w <= f.x)
                    left = Math.max(left, (g.x + g.w + f.x) / 2);
                else if (g.x >= f.x + f.w)
                    right = Math.min(right, (f.x + f.w + g.x) / 2);
            }
        }
        out.set(f.id, { x: left, y: top, w: right - left, h: bottom - top });
    }
    return out;
}
// ── 문서 표시명(canonical) ────────────────────────────────────────────────
// sign-console-prefill-2026-07 — 서명 문서는 어디서나 양식명이 아니라
// `{고객사}_{문서유형}_{YYMMDD}` 로 표시·저장한다. 라벨/파일명 진실원(중복 제거).
/** 서명 문서유형 → 한글 라벨(표시·파일명 공통). */
export const SIGNATURE_DOCUMENT_TYPE_LABELS = {
    DELIVERY_RECEIPT: '인수증',
    INSTALLATION_TRAINING: '설치·교육 인수증',
    REMOTE_SERVICE_CONSENT: '데이터 수집·이용 동의서',
    CUSTOM: '서명 문서',
};
/**
 * 서명 문서 표시명/파일명 베이스 = `{고객사}_{문서유형}_{YYMMDD}`.
 * date 는 문서 생성일(모든 상태에 존재). YYMMDD 는 KST 기준. 파일명 사용 시엔
 * 호출부에서 금지문자 제거 + `.pdf` 를 붙인다(표시엔 원문 그대로).
 */
export function signatureDocumentDisplayName(customerName, documentType, date) {
    const cust = (customerName ?? '').trim() || '고객사';
    const label = SIGNATURE_DOCUMENT_TYPE_LABELS[documentType] ?? '서명 문서';
    const d = typeof date === 'string' ? new Date(date) : date;
    // 방어: 잘못된/누락 날짜엔 날짜 세그먼트를 생략(크래시 방지).
    const ms = d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : NaN;
    if (Number.isNaN(ms))
        return `${cust}_${label}`;
    // 추가수정 2차(26-07-27) 정정4 — 오프셋만 정본 상수로 교체. 출력 문자열
    // (`YYMMDD`)은 문서 표시명·파일명 규약이라 **절대 바꾸지 않는다** — kstYmd 의
    // 점 표기로 갈아타지 않고 여기서 slice 하는 이유.
    const kst = new Date(ms + KST_OFFSET_MS);
    const yymmdd = kst.toISOString().slice(2, 10).replace(/-/g, '');
    return `${cust}_${label}_${yymmdd}`;
}
//# sourceMappingURL=signature.js.map